import {
  IconButton,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerFooter,
  Button,
  Stack,
  FormControl,
  FormLabel,
  Select,
  Switch,
  useDisclosure,
  Tooltip,
  Text,
  Divider,
} from '@chakra-ui/react';
import { SettingsIcon } from '@chakra-ui/icons';
import { useRef } from 'react';
import { useUserPreferences } from '../context/UserPreferencesContext';
import { FilterState } from '../types/vulnerability';

const analysisOptions: Array<{ label: string; value: FilterState['analysisMode'] }> = [
  { label: 'All Vulnerabilities', value: 'all' },
  { label: 'Analysis (exclude invalid - norisk)', value: 'analysis' },
  { label: 'AI Analysis (exclude ai-invalid-norisk)', value: 'ai-analysis' },
];

export default function PreferencesDrawer() {
  const { preferences, updatePreferences } = useUserPreferences();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const firstField = useRef<HTMLButtonElement>(null);

  const handleModeChange = (mode: FilterState['analysisMode']) => {
    updatePreferences((prev) => ({
      ...prev,
      defaultAnalysisMode: mode,
    }));
  };

  const handleDensityChange = (density: 'comfortable' | 'compact') => {
    updatePreferences((prev) => ({
      ...prev,
      tableDensity: density,
    }));
  };

  const handlePaletteChange = (palette: 'default' | 'colorBlindFriendly' | 'nightMode') => {
    updatePreferences((prev) => ({
      ...prev,
      preferredPalette: palette,
    }));
  };

  const handleToggle = (key: 'enableAnimations' | 'showTrendLines') => {
    updatePreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <>
      <Tooltip label="Dashboard preferences" openDelay={300}>
        <IconButton
          aria-label="Open preferences"
          icon={<SettingsIcon />}
          variant="ghost"
          colorScheme="brand"
          onClick={onOpen}
          ref={firstField}
        />
      </Tooltip>

      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={onClose}
        initialFocusRef={firstField}
        size="sm"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            Personalize your dashboard
          </DrawerHeader>

          <DrawerBody>
            <Stack spacing={6} mt={4}>
              <FormControl>
                <FormLabel>Default analysis mode</FormLabel>
                <Select
                  value={preferences.defaultAnalysisMode}
                  onChange={(event) => handleModeChange(event.target.value as FilterState['analysisMode'])}
                >
                  {analysisOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Determines which analysis mode loads when you visit the dashboard.
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel>Table density</FormLabel>
                <Select
                  value={preferences.tableDensity}
                  onChange={(event) => handleDensityChange(event.target.value as 'comfortable' | 'compact')}
                >
                  <option value="comfortable">Comfortable</option>
                  <option value="compact">Compact</option>
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Compact mode shows more rows without scrolling.
                </Text>
              </FormControl>

              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel mb="0">Enable micro-animations</FormLabel>
                <Switch
                  isChecked={preferences.enableAnimations}
                  onChange={() => handleToggle('enableAnimations')}
                  colorScheme="brand"
                />
              </FormControl>

              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <FormLabel mb="0">Show trend lines</FormLabel>
                <Switch
                  isChecked={preferences.showTrendLines}
                  onChange={() => handleToggle('showTrendLines')}
                  colorScheme="brand"
                />
              </FormControl>

              <Divider />

              <FormControl>
                <FormLabel>Chart palette</FormLabel>
                <Select
                  value={preferences.preferredPalette}
                  onChange={(event) => handlePaletteChange(event.target.value as 'default' | 'colorBlindFriendly' | 'nightMode')}
                >
                  <option value="default">Vivid (default)</option>
                  <option value="colorBlindFriendly">Color-blind friendly</option>
                  <option value="nightMode">Night mode</option>
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Applies enhanced contrast palettes for charts.
                </Text>
              </FormControl>
            </Stack>
          </DrawerBody>

          <DrawerFooter borderTopWidth="1px">
            <Button variant="outline" mr={3} onClick={onClose}>
              Close
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}
